/**
 * Axios リトライインターセプター
 *
 * フロントエンドからバックエンドAPIへのリクエストに対するリトライ処理
 */

import { AxiosError, AxiosInstance } from "axios";

export interface AxiosRetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  retryableStatuses: number[];
  retryableErrors: string[];
}

/**
 * デフォルトのリトライ設定
 */
const DEFAULT_RETRY_CONFIG: AxiosRetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000, // 1秒
  maxDelay: 10000, // 10秒
  retryableStatuses: [408, 429, 500, 502, 503, 504],
  retryableErrors: [
    "ECONNABORTED",
    "ETIMEDOUT",
    "ENOTFOUND",
    "ENETUNREACH",
    "ERR_NETWORK",
  ],
};

/**
 * スリープ
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 遅延時間を計算（Exponential backoff + jitter）
 */
function calculateDelay(attempt: number, config: AxiosRetryConfig): number {
  const exponentialDelay = config.baseDelay * Math.pow(2, attempt - 1);
  const cappedDelay = Math.min(exponentialDelay, config.maxDelay);
  const jitter = Math.random() * 0.5 * cappedDelay; // 0〜50%のジッター
  return cappedDelay + jitter;
}

/**
 * リトライ対象のエラーか判定
 */
function isRetryableError(
  error: AxiosError,
  config: AxiosRetryConfig
): boolean {
  // ブラウザがオフラインを検知している場合は、リトライしても無駄なので即諦める。
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return false;
  }

  // ネットワークエラー
  if (error.code && config.retryableErrors.includes(error.code)) {
    return true;
  }

  // HTTPステータスコード
  if (
    error.response &&
    config.retryableStatuses.includes(error.response.status)
  ) {
    return true;
  }

  // タイムアウト
  if (error.message?.includes("timeout")) {
    return true;
  }

  return false;
}

/**
 * Axiosインスタンスにリトライ機能を追加
 */
export function setupAxiosRetry(
  axiosInstance: AxiosInstance,
  customConfig?: Partial<AxiosRetryConfig>
): void {
  const config: AxiosRetryConfig = { ...DEFAULT_RETRY_CONFIG, ...customConfig };

  axiosInstance.interceptors.response.use(
    // 成功時はそのまま返す
    (response) => response,

    // エラー時のリトライ処理
    async (error: AxiosError) => {
      const originalConfig: any = error.config;

      // リトライ回数の初期化
      if (!originalConfig._retryCount) {
        originalConfig._retryCount = 0;
      }

      // リトライ可能かチェック
      if (
        originalConfig._retryCount >= config.maxAttempts ||
        !isRetryableError(error, config)
      ) {
        return Promise.reject(error);
      }

      // リトライ回数をインクリメント
      originalConfig._retryCount++;

      // Retry-Afterヘッダーの確認
      const retryAfter = error.response?.headers["retry-after"];
      let delay = calculateDelay(originalConfig._retryCount, config);

      if (retryAfter) {
        const retryAfterMs = parseInt(retryAfter) * 1000;
        delay = Math.max(delay, retryAfterMs);
      }

      // ログ出力
      console.warn(
        `[Axios Retry] Attempt ${originalConfig._retryCount}/${config.maxAttempts}`,
        {
          url: originalConfig.url,
          method: originalConfig.method?.toUpperCase(),
          status: error.response?.status,
          code: error.code,
          delay: `${Math.round(delay)}ms`,
        }
      );

      // 待機してリトライ
      await sleep(delay);
      return axiosInstance(originalConfig);
    }
  );
}
